/**
 * Marca en runtime — modo de presentación sin marca de cliente.
 *
 * POR QUÉ RUNTIME Y NO UN BUILD APARTE. El visor lo usan a diario los usuarios
 * reales del cliente. Un deploy «sin logos» que hay que revertir a mano deja a
 * esos usuarios sin su marca mientras dure, y depende de que alguien se acuerde
 * de revertirlo. Este modo vive en el navegador de quien presenta: se enciende
 * con un parámetro de URL, se apaga cerrando la ventana, y **nadie más lo ve**.
 *
 * CÓMO SE ENCIENDE Y SE APAGA.
 *   ?marca=neutra   → activa y persiste en localStorage de ESE origen.
 *   ?marca=cliente  → apaga y borra la marca persistida.
 *   sin parámetro   → lo que diga localStorage; por omisión, el cliente actual.
 *
 * ALCANCE POR ORIGEN. `localStorage` no se comparte entre `elawan.*`,
 * `elawan-dashboard.*` y `elawan-equipo.*` — son tres orígenes distintos. El
 * flag hay que encenderlo en cada uno. Para no depender de eso el día de la
 * reunión, el login del landing propaga `?marca=neutra` al subdominio destino
 * (ver `propagarMarca`).
 *
 * REGLA DURA. Sin el flag, `perfilMarca()` devuelve `CLIENTE_ACTUAL`, que es
 * palabra por palabra lo que el visor renderiza hoy. El modo neutro no puede
 * cambiar ni un pixel por omisión.
 *
 * LO QUE ESTO **NO** HACE. Neutralizar la marca no neutraliza los datos. Con el
 * modo activo, el portafolio en pantalla sigue siendo el del cliente real
 * (parcelas, ejidos, titulares, negociaciones). Qué proyecto se enseña es una
 * decisión de operación, no de este módulo.
 */

const CLAVE_STORAGE = 'aosenuma:marca';
const PARAM_URL = 'marca';
const VALOR_NEUTRA = 'neutra';
const VALOR_CLIENTE = 'cliente';

export interface PerfilMarca {
  /** `true` solo en modo presentación. Las vistas ramifican sobre esto. */
  readonly neutral: boolean;
  /** Nombre largo. Cabeceras y firmas de documentos exportados. */
  readonly clienteNombre: string;
  /** Nombre corto. Chips, toggles, banners, textos corridos. */
  readonly clienteCorto: string;
  /**
   * El cliente como parte firmante. En neutro no sirve el nombre de producto
   * («Revisó: aosenuma report» no se lee como una firma), sino el rol.
   */
  readonly contraparte: string;
  /** Proyecto. `<title>`, meta description, encabezado de reportes. */
  readonly proyecto: string;
  /** Placeholder del correo en la tarjeta de acceso. */
  readonly correoEjemplo: string;
  /** Etiqueta del rol cliente en el toggle de acceso. */
  readonly etiquetaRolCliente: string;
  /** Pie de página de PDF y XLSX exportados. */
  readonly pieDocumento: string;
  /** Prefijo de los archivos que se descargan. */
  readonly prefijoArchivo: string;
  /** Asunto del `mailto:` de ayuda. */
  readonly asuntoAyuda: string;
  /**
   * En modo neutro el producto ES aosenuma, así que el «powered by aosenuma»
   * que acompaña al logo del cliente sobra y se lee como duplicado.
   */
  readonly ocultarPoweredBy: boolean;
}

/** Lo que el visor muestra hoy. No tocar sin cambiar producción a propósito. */
export const MARCA_CLIENTE_ACTUAL: PerfilMarca = {
  neutral: false,
  clienteNombre: 'Elawan Energy',
  clienteCorto: 'Elawan',
  contraparte: 'Elawan Energy',
  proyecto: 'Elawan Huimilpan',
  correoEjemplo: 'nombre@elawan.com',
  etiquetaRolCliente: 'Cliente · Elawan',
  pieDocumento: 'Elawan Energy · powered by aosenuma · Confidencial',
  prefijoArchivo: 'elawan',
  asuntoAyuda: 'Ayuda panel Elawan',
  ocultarPoweredBy: false,
};

/** Modo presentación. Sobrio, sin logo inventado y sin colores nuevos. */
export const MARCA_NEUTRA: PerfilMarca = {
  neutral: true,
  clienteNombre: 'aosenuma report',
  clienteCorto: 'aosenuma report',
  contraparte: 'Cliente',
  proyecto: 'aosenuma report',
  correoEjemplo: 'nombre@empresa.com',
  etiquetaRolCliente: 'Cliente',
  pieDocumento: 'aosenuma report · Confidencial',
  prefijoArchivo: 'aosenuma_report',
  asuntoAyuda: 'Ayuda aosenuma report',
  ocultarPoweredBy: true,
};

/**
 * Núcleo puro: decide y persiste. Separado del singleton para poder probarlo
 * sin un `window` real.
 */
export function resolverMarcaNeutra(
  search: string,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
): boolean {
  let param: string | null = null;
  try {
    param = new URLSearchParams(search).get(PARAM_URL);
  } catch {
    param = null;
  }

  try {
    if (param === VALOR_NEUTRA) storage.setItem(CLAVE_STORAGE, VALOR_NEUTRA);
    else if (param === VALOR_CLIENTE) storage.removeItem(CLAVE_STORAGE);
    return storage.getItem(CLAVE_STORAGE) === VALOR_NEUTRA;
  } catch {
    // Safari en modo privado puede lanzar al escribir. El parámetro sigue
    // valiendo para esta carga aunque no se pueda persistir.
    return param === VALOR_NEUTRA;
  }
}

/**
 * Se resuelve una sola vez por carga: si cambiara a mitad de render, media
 * pantalla saldría con una marca y media con la otra.
 */
let cache: boolean | null = null;

export function marcaNeutraActiva(): boolean {
  if (cache === null) {
    cache =
      typeof window === 'undefined'
        ? false
        : resolverMarcaNeutra(window.location.search, window.localStorage);
  }
  return cache;
}

/** La única puerta por la que las vistas piden marca. */
export function perfilMarca(): PerfilMarca {
  return marcaNeutraActiva() ? MARCA_NEUTRA : MARCA_CLIENTE_ACTUAL;
}

/** Solo para tests: olvida la resolución memoizada. */
export function _reiniciarCacheMarca(): void {
  cache = null;
}

/**
 * Arrastra el modo al saltar de origen (landing → dashboard/equipo), que es
 * donde `localStorage` deja de servir. No-op si el modo está apagado, así que
 * la URL de todos los días no cambia.
 */
export function propagarMarca(url: string): string {
  if (!marcaNeutraActiva()) return url;
  try {
    const u = new URL(url);
    u.searchParams.set(PARAM_URL, VALOR_NEUTRA);
    return u.toString();
  } catch {
    return url;
  }
}
